/**
 * React hook для получения контактной информации
 * Приоритет: URL параметры > iframe data-атрибуты > defaults
 */

import { useMemo } from 'react';
import { useContactParams } from '../context/ContactParamsContext.jsx';
import { config } from '../config/runtimeConfig.js';
import { isBusinessOpen } from '../utils/businessHours.js';

/**
 * Default контактная информация (fallback значения)
 */
/** Запасная подпись компании витрины (#1431: Footer переводит только её). */
export const DEFAULT_COMPANY_DESCRIPTION =
  'A demo dealership showing how the ShiftGears assistant works on a real showroom site.';

const DEFAULT_CONTACT_INFO = {
  phone: '+44 7418 613962',
  email: 'info@shiftgears.ai',
  // ⛔ Пусто намеренно. Здесь стоял `+35799006345` — боевой WhatsApp Andy's
  // Motors, приехавший вместе с коммитом «Add Andy's Motors integration test
  // page». Последствия шире витрины: `useContactInfo` подставляет этот дефолт
  // ЛЮБОМУ тенанту, у которого не заполнен `contactWhatsapp`, — то есть чужие
  // покупатели писали бы в WhatsApp другого дилера. Пока у ShiftGears нет
  // своего номера, канал остаётся ненастроенным: `openWhatsApp` на пустом
  // телефоне не открывает ничего, а блок мессенджеров рисует карточку без
  // ссылки. Номер задаётся через `?contactWhatsapp=`, `data-contact-whatsapp`
  // или `VITE_CONTACT_WHATSAPP`.
  whatsapp: '',
  // Telegram-бот витрины (#1336). Пусто намеренно: подключённого бота у демо
  // ещё нет — `docs/messengers/telegram-setup.md` прямо пишет, что засеянная
  // строка с меткой `demo` это placeholder, который не умеет ни принимать, ни
  // отправлять. Пока хендл не задан, канал показывается без ссылки: мёртвый
  // `t.me/...` на витрине хуже отсутствующей ссылки.
  telegram: '',
  // Per-day schedule. Business-days share the same default 9-18 window;
  // Sat 9-17; Sun default closed (omitted). Format matches isBusinessOpen parser.
  hours: {
    mon: 'Monday: 9:00 - 18:00',
    tue: 'Tuesday: 9:00 - 18:00',
    wed: 'Wednesday: 9:00 - 18:00',
    thu: 'Thursday: 9:00 - 18:00',
    fri: 'Friday: 9:00 - 18:00',
    sat: 'Saturday: 9:00 - 17:00',
    sun: 'Sunday: 11:00 - 16:00',
  },
  // Легенда витрины (#1330): вымышленный дилер под нашим брендом, рынки —
  // Кипр и Люксембург. Кипр потому, что это рынок компании; Люксембург —
  // потому что демо-инвентарь приезжает от люксембургского дилера, и
  // французские значения в карточках получают объяснение.
  //
  // Адреса намеренно указывают на публичные ориентиры городов, а не на
  // конкретные здания: витрина не должна отправлять человека по адресу, где
  // никакого салона нет.
  locations: [
    {
      name: 'Nicosia',
      address: 'Makarios III Avenue',
      city: '1065 Nicosia',
      country: 'Cyprus',
      // Координаты нужны карте салонов (`LocationsMap`): по ним ставятся
      // метки. Держим их рядом с адресом, чтобы подпись и карта не могли
      // разъехаться.
      coords: { lat: 35.1657, lon: 33.3643 },
      mapsUrl: 'https://maps.google.com/?q=Makarios+III+Avenue+Nicosia+Cyprus',
    },
    {
      name: 'Luxembourg',
      address: 'Route d\'Esch',
      city: 'L-1470 Luxembourg',
      country: 'Luxembourg',
      coords: { lat: 49.5975, lon: 6.116 },
      mapsUrl: 'https://maps.google.com/?q=Route+d+Esch+Luxembourg',
    },
  ],
  company: {
    name: 'ShiftGears Motors',
    description: DEFAULT_COMPANY_DESCRIPTION,
  },
};

/**
 * #1323: остальные дефолты выше — витрина демо, и на demo.shiftgears.ai они
 * уместны. Имя компании — нет: в widget mode мы стоим на сайте дилера, и
 * подпись «© ShiftGears Motors» в его подвале — не заглушка, а чужая
 * вывеска. Не знаем имени — не подписываем; подвал сам уберёт пустое место.
 */
function defaultCompanyName() {
  return config.ENVIRONMENT_NAME === 'widget' ? '' : DEFAULT_CONTACT_INFO.company.name;
}

/**
 * То же правило для часов работы: демо-расписание — факт о демо-витрине.
 * Напечатать «Monday: 9:00 - 18:00» в подвале дилера, который часы не
 * настраивал, — не заглушка, а выдуманное утверждение о его бизнесе.
 */
function defaultHours(day) {
  return config.ENVIRONMENT_NAME === 'widget' ? '' : DEFAULT_CONTACT_INFO.hours[day];
}

/**
 * Hook для получения контактной информации с учетом URL/iframe параметров
 * @returns {Object} Контактная информация
 */
export function useContactInfo() {
  // Получаем параметры из глобального реактивного стейта
  const params = useContactParams();

  const contactInfo = useMemo(() => {

    // Собираем контактную информацию с приоритетом: params > defaults
    const info = {
      phone: params.contactPhone || DEFAULT_CONTACT_INFO.phone,
      email: params.contactEmail || DEFAULT_CONTACT_INFO.email,
      whatsapp: params.contactWhatsapp || DEFAULT_CONTACT_INFO.whatsapp,
      telegram: params.contactTelegram || DEFAULT_CONTACT_INFO.telegram,
      hours: {
        mon: params.hoursMon || defaultHours('mon'),
        tue: params.hoursTue || defaultHours('tue'),
        wed: params.hoursWed || defaultHours('wed'),
        thu: params.hoursThu || defaultHours('thu'),
        fri: params.hoursFri || defaultHours('fri'),
        sat: params.hoursSat || defaultHours('sat'),
        sun: params.hoursSun || defaultHours('sun'),
      },
      locations: [],
      company: {
        name: params.companyName || defaultCompanyName(),
        description: params.companyDescription || DEFAULT_CONTACT_INFO.company.description,
      },
    };

    // Собираем локации
    // Location 1
    if (params.location1Name || params.location1Address) {
      info.locations.push({
        name: params.location1Name || 'Main Showroom',
        address: params.location1Address || '',
        city: params.location1City || '',
        country: params.location1Country || '',
        mapsUrl: params.location1Maps || '',
      });
    }

    // Location 2 (optional)
    if (params.location2Name || params.location2Address) {
      info.locations.push({
        name: params.location2Name || 'Secondary Showroom',
        address: params.location2Address || '',
        city: params.location2City || '',
        country: params.location2Country || '',
        mapsUrl: params.location2Maps || '',
      });
    }

    // Если нет кастомных локаций, используем defaults
    if (info.locations.length === 0) {
      info.locations = DEFAULT_CONTACT_INFO.locations;
    }

    // Дериват: открыт ли дилер прямо сейчас (по часам работы + локальному времени пользователя).
    // Используется для переключения мобильной CTA: Call Now в рабочие часы → Ask AI в нерабочие.
    info.isOpen = isBusinessOpen(info.hours);

    console.log('[useContactInfo] Final contact info:', info);

    return info;
  }, [params]); // Пересчитывается при изменении params из глобального стейта

  return contactInfo;
}
